import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

import { cn } from "@repo/ui/lib/utils";
import { ButtonProps, buttonVariants } from "@repo/ui/components/ui/button";
import { ComponentProps, forwardRef } from "react";
import { Popover, PopoverClose, PopoverTrigger } from "@radix-ui/react-popover";
import { PopoverContent } from "@repo/ui/components/ui/popover";
import Icon from "@repo/ui/components/ui/icon/icon";
import { ScrollArea } from "@repo/ui/components/ui/scroll-area";

type NumberedPaginationProps = {
  totalPages: number;
  currentPage: number;
  /* eslint-disable-next-line no-unused-vars */
  onPageSelected: (page: number) => void;
};

const Pagination = ({ className, ...props }: ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

const PaginationContent = forwardRef<HTMLUListElement, ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  ),
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = forwardRef<HTMLLIElement, ComponentProps<"li">>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn("", className)} {...props} />
  ),
);
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size"> &
  ComponentProps<"a">;

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: "ghost",
        size,
      }),
      { "border-2 border-secondary-black": isActive },
      "cursor-pointer h-[40px] rounded-[5px] bg-secondary-gray1 p-2 text-[16px] font-normal",
      "hover:bg-secondary-gray1/80",
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
    <span>Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>Next</span>
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn(
      "flex h-9 w-12 items-center justify-center",
      "cursor-pointer h-[40px] rounded-[5px] bg-secondary-gray1 p-2 text-[16px] font-normal",
      className,
    )}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

const NumberedPagination = ({
  totalPages,
  currentPage,
  onPageSelected,
}: NumberedPaginationProps) => {
  function createArray(total: number) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const start = [1, 2, 3];
    const hidden = Array.from({ length: total - 6 }, (_, i) => i + 4);
    const end = [total - 2, total - 1, total];

    return [...start, hidden, ...end];
  }

  return createArray(totalPages).map((page) => {
    if (Array.isArray(page)) {
      return (
        <PaginationItem key={`page_${page}`}>
          <Popover>
            <PopoverTrigger>
              <PaginationEllipsis />
            </PopoverTrigger>
            <PopoverContent
              align="center"
              className="rounded-tr-[10px] overflow-hidden"
            >
              <ScrollArea className="h-72 w-[70px] pl-3">
                {page.map((item, i) => (
                  <PopoverClose
                    asChild
                    className="cursor-pointer py-1 flex items-center gap-2"
                  >
                    <div key={i} onClick={() => onPageSelected(item - 1)}>
                      <span
                        className={cn("font-bold", {
                          "font-normal": item !== currentPage + 1,
                        })}
                      >
                        {item}
                      </span>
                      <Icon
                        icon="Check"
                        className={cn("h-[14px] w-[14px]", {
                          hidden: item !== currentPage + 1,
                        })}
                      />
                    </div>
                  </PopoverClose>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </PaginationItem>
      );
    }

    return (
      <PaginationItem key={`page_${page}`}>
        <PaginationLink
          isActive={page === currentPage + 1}
          onClick={() => onPageSelected(page - 1)}
        >
          {page}
        </PaginationLink>
      </PaginationItem>
    );
  });
};

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  NumberedPagination,
};
